import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { environment } from 'src/app/environments/environment';
import { BehaviorSubject } from 'rxjs';

export interface ChatMessage {
  id?: string;
  sender_id: string;
  sender_type?: string;
  sender_name?: string;
  receiver_id?: string | null;
  receiver_type?: string | null;
  message: string;
  thread_id: string;
  is_group: true;
  messageStatus?: string;
  created_at?: string;
}

@Injectable({
  providedIn: 'root'
})
export class GroupChatService {
  private socket: Socket;
  private messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  public messages$ = this.messagesSubject.asObservable();

  private typingSubject = new BehaviorSubject<any>(null);
  public typing$ = this.typingSubject.asObservable();

  private unreadCountSubject = new BehaviorSubject<{ [senderId: string]: number }>({});
  public unreadCount$ = this.unreadCountSubject.asObservable();

  public userId: string | null = null;

  private currentThreadId: string | null = null;
  // private userId: string | null = null;
  totalUnreadCount$: any;

  constructor() {
    this.socket = io(environment.serverUrl);

    this.socket.on('connect', () => {
      console.log(' Connected to socket server:', this.socket.id);
    });

    this.socket.on('receiveMessage', (message: ChatMessage) => {
      if (
        message.is_group &&
        message.thread_id === this.currentThreadId &&
        message.sender_id !== this.userId
      ) {
        this.addMessage(message);
      }
    });

    this.socket.on('messageSent', (message: ChatMessage) => {
      if (
        message.is_group &&
        message.thread_id === this.currentThreadId &&
        message.sender_id === this.userId
      ) {
        this.addMessage(message);
      }
    });

    this.socket.on('messageDeleted', ({ message_id }) => {
      const currentMessages = this.messagesSubject.getValue();
      const filtered = currentMessages.filter(msg => msg.id !== message_id);
      this.messagesSubject.next(filtered);
    });

    this.socket.on('typing', (data: { sender_id: string; thread_id: string; isTyping: boolean; sender_name?: string }) => {
      if (data.thread_id === this.currentThreadId) {
        this.typingSubject.next(data);
      }
    });


    this.socket.on('unreadCountsUpdated', (counts: { [senderId: string]: number }) => {
      console.log(" Unread counts updated:", counts);
      this.unreadCountSubject.next(counts);
    });
  }

  joinGroupChat(userId: string, threadId: string) {
    this.userId = userId;
    this.currentThreadId = threadId;

    this.socket.emit('join', userId);
    this.socket.emit('join', threadId);


    this.socket.emit('getUnreadCounts', { userId }, (response: any) => {
      if (response.success) {
        console.log("Initial unread counts:", response.counts);
        this.unreadCountSubject.next(response.counts);
      } else {
        console.error(" Failed to get unread counts:", response.error);
      }
    });

    this.loadChatHistory(threadId);
  }

  sendMessage(message: string) {
    if (!this.userId || !this.currentThreadId) {
      console.error(' User ID or Thread ID not set');
      return;
    }

    const msgData: ChatMessage = {
      sender_id: this.userId,
      sender_type: 'employee',
      receiver_id: null,
      receiver_type: null,
      message,
      is_group: true,
      thread_id: this.currentThreadId,
      messageStatus: 'sent'
    };

    this.socket.emit('sendMessage', msgData);
  }

  private addMessage(message: ChatMessage) {
    const currentMessages = this.messagesSubject.getValue();
    this.messagesSubject.next([...currentMessages, message]);
  }

  deleteMessage(messageId: string) {
    if (!this.userId) return;

    this.socket.emit('deleteMessage', {
      message_id: messageId,
      user_id: this.userId
    });
  }

  loadChatHistory(threadId: string, limit = 50, offset = 0) {
    this.socket.emit('getChatHistory', {
      thread_id: threadId,
      is_group: true,
      limit,
      offset
    });

    this.socket.once('chatHistory', (data: { success: boolean; data: ChatMessage[] }) => {
      if (data.success) {
        this.messagesSubject.next(data.data);
      }
    });
  }

  sendTypingStatus(isTyping: boolean) {
    if (!this.userId || !this.currentThreadId) return;

    this.socket.emit('typing', {
      sender_id: this.userId,
      thread_id: this.currentThreadId,
      isTyping
    });
  }



  getGroupUnreadCount(thread_id: string): Promise<number> {
    if (!this.userId || !thread_id) {
      console.error(' Missing userId or threadId');
      return Promise.resolve(0);
    }

    return new Promise((resolve, reject) => {
      this.socket.emit(
        'getGroupUnreadCount',
        { employee_id: this.userId, thread_id },
        (response: any) => {
          if (response.success) {
            console.log(`Unread count for thread ${thread_id}:`, response.unreadCount);
            resolve(response.unreadCount);
          } else {
            console.error(' Failed to get group unread count:', response.error);
            reject(response.error);
          }
        }
      );
    });
  }


  markGroupMessagesAsRead(thread_id: string): void {
    if (!this.userId || !thread_id) {
      console.error('Missing userId or threadId');
      return;
    }

    this.socket.emit('markGroupMessagesAsRead', {
      employee_id: this.userId,
      thread_id
    });

    // Optional: update local unread count state
    const currentCounts = this.unreadCountSubject.getValue();
    currentCounts[thread_id] = 0;
    this.unreadCountSubject.next({ ...currentCounts });
  }



  disconnect() {
    this.socket.disconnect();
  }
}